import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin } from "rxjs";
import { ApiService } from "./api.service";
import { RegionDto } from "../models/region-dto";
import { ExpeditionDto } from "../models/expedition-dto";
import { ExcavationDto } from "../models/excavation-dto";
import { SurveyDto } from "../models/survey-dto";
import { LicenseDto } from "../models/license-dto";
import { WordDto } from "../models/word-dto";
import { TreeNode } from "primeng/api";
import { EntityTypeDto } from "../models/entity-type-dto";
import { map, takeUntil, tap } from 'rxjs/operators';
import { ThesaurusTreeAllDto, TreeNodeDto } from "../models/tree-node-dto";
import { TreeService } from "./tree.service";
import { FieldsListsDto } from "../models/fields-lists-dto";
import { HachrazaMiniDto } from "../models/hachraza-mini-dto";
import { CustomTreeNode } from "../models/custom-tree-node";
import { PersonDto } from "../models/person-dto";
import { LanguageService } from "./language.service";
import { SettlementDto } from "../models/settlement";
import { MultiLangStrDto } from "../components/module-header/header-interfaces";

@Injectable({
  providedIn: 'root'
})
export class FieldsListsService implements OnDestroy {
  private allFieldsLoadedSubject = new Subject<boolean>();
  allFieldsLoaded$ = this.allFieldsLoadedSubject.asObservable();

  private regionsSubject = new BehaviorSubject<RegionDto[]>([]);
  regions$ = this.regionsSubject.asObservable();

  private settlementsSubject = new BehaviorSubject<SettlementDto[]>([]);
  settlements$ = this.settlementsSubject.asObservable();

  private regionPolygonsSubject = new BehaviorSubject<string[]>([]);
  regionPolygons$ = this.regionPolygonsSubject.asObservable();

  private expeditionsSubject = new BehaviorSubject<ExpeditionDto[]>([]);
  expeditions$ = this.expeditionsSubject.asObservable();

  private excavationsSubject = new BehaviorSubject<ExcavationDto[]>([]);
  excavations$ = this.excavationsSubject.asObservable();

  private surveysSubject = new BehaviorSubject<SurveyDto[]>([]);
  surveys$ = this.surveysSubject.asObservable();

  private personsSubject = new BehaviorSubject<PersonDto[]>([]);
  persons$ = this.personsSubject.asObservable();

  private hachrazotSubject = new BehaviorSubject<HachrazaMiniDto[]>([]);
  hachrazot$ = this.hachrazotSubject.asObservable();

  private licensesSubject = new BehaviorSubject<LicenseDto[]>([]);
  licenses$ = this.licensesSubject.asObservable();

  private thesaurusSubject = new BehaviorSubject<WordDto[]>([]);
  thesaurus$ = this.thesaurusSubject.asObservable();

  private thesaurusAllSubject = new BehaviorSubject<ThesaurusTreeAllDto>({ categories: [] });
  thesaurusAll$ = this.thesaurusAllSubject.asObservable();

  private periodsSubject = new BehaviorSubject<CustomTreeNode>({});
  periods$ = this.periodsSubject.asObservable();

  private materialsSubject = new BehaviorSubject<CustomTreeNode>({});
  materials$ = this.materialsSubject.asObservable();

  private heritageSitesSubject = new BehaviorSubject<CustomTreeNode>({});
  heritageSites$ = this.heritageSitesSubject.asObservable();

  private findTypesSubject = new BehaviorSubject<CustomTreeNode>({});
  findTypes$ = this.findTypesSubject.asObservable();


  private entityTypesSubject = new BehaviorSubject<EntityTypeDto[]>([]);
  entityTypes$ = this.entityTypesSubject.asObservable();


  private collators: { [lang: string]: Intl.Collator } = {};

  fieldsLists$: Observable<FieldsListsDto> = combineLatest([
    this.settlements$,
    this.regions$,
    this.persons$,
    this.expeditions$,
    this.excavations$,
    this.surveys$,
    this.hachrazot$,
    this.licenses$,
    this.thesaurus$,
    this.thesaurusAll$,
    this.periods$,
    this.materials$,
    this.findTypes$,
    this.entityTypes$,
    this.regionPolygons$,
    this.heritageSites$
  ]).pipe(
    map(([settlements, regions, persons, expeditions, excavations, surveys, hachrazot, licenses, thesaurus, thesaurusAll, periods, materials, findTypes, entityTypes, regionPolygons, heritageSites]) => ({
      settlements,
      regions,
      persons,
      expeditions,
      excavations,
      surveys,
      hachrazot,
      licenses,
      thesaurus,
      thesaurusAll,
      periods,
      materials,
      findTypes,
      entityTypes,
      regionPolygons,
      heritageSites
    }))
  );
  currentLang!: string;
  private destroy$ = new Subject<void>();

  constructor(private apiService: ApiService, private treeService: TreeService, private languageService: LanguageService) {
    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => {
        this.currentLang = lang;
        this.resortingAllListsData();
      });
  }

  loadRegions(): Observable<RegionDto[]> {
    return this.apiService.getRegions().pipe(
      map(regions =>
        this.compare(regions)
      ),
      tap(sortedRegions => this.regionsSubject.next(sortedRegions))
    );
  }

  compare(arr: any[]): any[] {
    const lang = this.languageService.currentLanguage.getValue() || 'he';
    return this.compareByLanguage(arr, lang === 'he' ? 'he' : 'en');
  }

  compareByLanguage(items: any[], currentLang: 'he' | 'en'): any[] {
    const hebRegex = /[\u0590-\u05FF]/;
    const engRegex = /[A-Za-z]/;

    const processed = items.map(item => {
      const heb = (item.name.hebrew || '').trim();
      const eng = (item.name.english || '').trim();

      let name, isHeb;
      if (currentLang === 'en') {
        name = eng && engRegex.test(eng) ? eng : heb || eng;
        isHeb = hebRegex.test(name);
      } else {
        name = heb && hebRegex.test(heb) ? heb : eng || heb;
        isHeb = hebRegex.test(name);
      }

      return { original: item, name, isHeb };
    });

    const primary = processed.filter(p => currentLang === 'en' ? !p.isHeb : p.isHeb);
    const secondary = processed.filter(p => currentLang === 'en' ? p.isHeb : !p.isHeb);

    const sort = (a:any, b:any) => {
      const keyA = a.name.replace(/^[^A-Za-z\u0590-\u05FF]*/, 'zzz') + a.name;
      const keyB = b.name.replace(/^[^A-Za-z\u0590-\u05FF]*/, 'zzz') + b.name;
      return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
    };

    return [...primary.sort(sort), ...secondary.sort(sort)].map(p => p.original);
  }



  loadSettlements(): Observable<SettlementDto[]> {
    return this.apiService.getSettlements().pipe(
      map(list => this.compare(list)),
      tap(sorted => this.settlementsSubject.next(sorted))
    );
  }

  loadPersons(): Observable<PersonDto[]> {
    return this.apiService.getPersons().pipe(
      map(persons =>
        this.compare(persons)
      ),
      tap(sortedPersons => this.personsSubject.next(sortedPersons))
    );
  }

  loadExpeditions(): Observable<ExpeditionDto[]> {
    return this.apiService.getExpeditions().pipe(
      map((expeditions: ExpeditionDto[]) => {
      return expeditions;
      }),
      tap(expeditions => { this.expeditionsSubject.next(expeditions) })
    );
  }

  loadExcavations(): Observable<ExcavationDto[]> {
    return this.apiService.getExcavations().pipe(
      map((excavations: ExcavationDto[]) => {
        return excavations;
      }),
      tap(excavations => { this.excavationsSubject.next(excavations) })
    );
  }

  loadSurveys(): Observable<SurveyDto[]> {
    return this.apiService.getSurveys().pipe(
      map((surveys: SurveyDto[]) => {
        return surveys;
      }),
      tap(surveys => { this.surveysSubject.next(surveys) })
    );
  }

  loadHachrazot(): Observable<HachrazaMiniDto[]> {
    return this.apiService.getHachrazot().pipe(
      map(hachrazot =>
        this.compare(hachrazot)
      ),
      tap(sortedHachrazot => this.hachrazotSubject.next(sortedHachrazot))
    );
  }

  loadLicenses(): Observable<LicenseDto[]> {
    return this.apiService.getLicenses().pipe(
      map((licenses: LicenseDto[]) => {
        const sortedLicenses = licenses.sort((a, b) => {
          return this.compareAlphabetically(a.longName, b.longName);
        });
        return sortedLicenses;
      }),
      tap(licenses => this.licensesSubject.next(licenses))
    );
  }

  loadThesaurus(): Observable<WordDto[]> {
    return this.apiService.getThesaurus().pipe(
      map((words: WordDto[]) => {
        const sortedWords = [...words]
        return sortedWords;
      }),
      tap(words => this.thesaurusSubject.next(words))
    );
  }

  loadPeriods(): Observable<CustomTreeNode<any>> {
    return this.apiService.getPeriods().pipe(
      map((periods: TreeNodeDto) => {
        const transformedPeriod = this.treeService.transformToTreeNode(periods);
        transformedPeriod.expanded = true;
        return transformedPeriod;
      }),
      tap(periods => this.periodsSubject.next(periods))
    );
  }

  loadMaterials(): Observable<CustomTreeNode<any>> {
    return this.apiService.getMaterials().pipe(
      map((materials: TreeNodeDto) => {
        const transformedMaterial = this.treeService.transformToTreeNode(materials);
        transformedMaterial.expanded = true;
        return transformedMaterial;
      }),
      tap(materials => this.materialsSubject.next(materials))
    )
  }

  loadHeritageSites(): Observable<CustomTreeNode<any>> {
    return this.apiService.getHeritageSites().pipe(
      map((heritageSites: TreeNodeDto) => {
        const transformed = this.treeService.transformToTreeNode(heritageSites);
        transformed.expanded = true;
        transformed.styleClass = 'all-categories-root';
        return transformed;
      }),
      tap(heritageSites => this.heritageSitesSubject.next(heritageSites))
    );
  }

  loadFindTypes(): Observable<CustomTreeNode<any>> {
    return this.apiService.getFindTypes().pipe(
      map((findTypes: TreeNodeDto) => {
        const transformedFindType = this.treeService.transformToTreeNode(findTypes);
        transformedFindType.expanded = true;
        return transformedFindType;
      }),
      tap(findTypes => this.findTypesSubject.next(findTypes))
    );
  }

  loadThesaurusAll(): Observable<ThesaurusTreeAllDto> {
    return this.apiService.getThesaurusAll().pipe(
      map((thesaurusAll: ThesaurusTreeAllDto[] | any) => {
        // If API returns an array, take the first element; otherwise use as-is
        const result = Array.isArray(thesaurusAll) && thesaurusAll.length > 0 
          ? thesaurusAll[0] 
          : (thesaurusAll.categories ? thesaurusAll : { categories: [] });
        
        // Transform tree nodes to CustomTreeNode format
        if (result.categories) {
          result.categories = result.categories.map((category: any) => ({
            categoryId: category.categoryId,
            categoryName: category.categoryName,
            tree: this.treeService.transformToTreeNode(category.tree)
          }));
        }
        
        return result;
      }),
      tap(thesaurusAll => this.thesaurusAllSubject.next(thesaurusAll))
    );
  }

  loadEntityTypes(): Observable<EntityTypeDto[]> {
    return this.apiService.getEntityTypes().pipe(
      map((entityTypes: EntityTypeDto[]) => {
        // const filteredEntityTypes = entityTypes.filter(
        //   (entityType: EntityTypeDto) =>
        //     entityType.id !== 'scientific_archive_folders' &&
        //     entityType.id !== 'manuscript'
        // );

        const sortedEntityTypes = entityTypes.sort((a, b) => {
          return this.compareAlphabetically(
            this.currentLang === 'he'
              ? a.entityTypeName.hebrew
              : a.entityTypeName.english,
            this.currentLang === 'he'
              ? b.entityTypeName.hebrew
              : b.entityTypeName.english
          );
        });

        return sortedEntityTypes;
      }),
      tap(entityTypes => this.entityTypesSubject.next(entityTypes))
    );
  }

  compareAlphabetically(strA: string, strB: string): number {
    const cleanA = strA.replace(/[^א-תa-zA-Z0-9]/g, '');
    const cleanB = strB.replace(/[^א-תa-zA-Z0-9]/g, '');
    const currentLang = this.languageService.currentLanguage.getValue();

    const startsWithNumberA = /^\d/.test(cleanA);
    const startsWithNumberB = /^\d/.test(cleanB);

    if (startsWithNumberA && !startsWithNumberB) {
      return 1;
    } else if (!startsWithNumberA && startsWithNumberB) {
      return -1;
    } else {
      const collator = this.getCollator(currentLang);
      return collator.compare(cleanA, cleanB);
    }
  }

  private getCollator(lang: string): Intl.Collator {
    if (!this.collators[lang]) {
      this.collators[lang] = new Intl.Collator(lang, { sensitivity: 'base' });
    }
    return this.collators[lang];
  }




  sortExpeditions(): void {
    const expeditions = [...this.expeditionsSubject.getValue()];
    const sortedExpeditions = expeditions.sort((a, b) => {
      const expeditionNameA = this.currentLang === 'he' ? a.expeditionName.hebrew : a.expeditionName.english;
      const expeditionNameB = this.currentLang === 'he' ? b.expeditionName.hebrew : b.expeditionName.english;
      return this.compareAlphabetically(expeditionNameA, expeditionNameB);
    });
    this.expeditionsSubject.next(sortedExpeditions);
  }
  sortSettlements(): void {
    const current = this.settlementsSubject.getValue();
    const sorted = this.compare(current);
    this.settlementsSubject.next(sorted);
  }
  sortPersons(): void {
    const current = this.personsSubject.getValue();
    const sorted = this.compare(current);
    this.personsSubject.next(sorted);
  }
  sortRegions(): void {
    const current = this.regionsSubject.getValue();
    const sorted = this.compare(current);
    this.regionsSubject.next(sorted);
  }
  sortHachrazot(): void {
    const current = this.hachrazotSubject.getValue();
    const sorted = this.compare(current);
    this.hachrazotSubject.next(sorted);
  }

  sortEntityTypes(): void {
    const entityTypes = [...this.entityTypesSubject.getValue()];
    // const filteredEntityTypes = entityTypes.filter(et => et.id !== 'scientific_archive_folders');
    const sortedEntityTypes = [...entityTypes].sort((a, b) => {
      const entityTypeNameA = this.currentLang === 'he' ? a.entityTypeName.hebrew : a.entityTypeName.english;
      const entityTypeNameB = this.currentLang === 'he' ? b.entityTypeName.hebrew : b.entityTypeName.english;
      return this.compareAlphabetically(entityTypeNameA, entityTypeNameB);
    });
    this.entityTypesSubject.next(sortedEntityTypes);
  }

  resortingAllListsData(): void {
    this.sortEntityTypes();
    this.sortSettlements();
    this.sortPersons();
    this.sortRegions();
    this.sortHachrazot();
  }


  loadAllFields(): void {
    forkJoin([
      this.loadThesaurusAll(),
      this.loadSettlements(),
      this.loadRegions(),
      this.loadPersons(),
      this.loadExpeditions(),
      this.loadExcavations(),
      this.loadSurveys(),
      this.loadHachrazot(),
      this.loadLicenses(),
      this.loadThesaurus(),
      this.loadPeriods(),
      this.loadMaterials(),
      this.loadFindTypes(),
      this.loadEntityTypes(),
      this.loadHeritageSites()
    ]).subscribe(() => {
      this.allFieldsLoadedSubject.next(true);
    });
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
